import { apiClient } from '@/api/client';
import type { PageOfWebhookDeliveryListItem, WebhookDeliveryDetail } from '@/api/types';
import type {
  TeamSettingsResponse,
  CreateTeamSettingsRequest,
  UpdateTeamSettingsRequest,
  UpdateTeamModelSelectionRequest,
  TeamModelAssignmentResponse,
  WebhookTestResponse,
  WebhookTestRequest,
} from '@/types/teamTypes';

export async function getTeamSettings(teamId: string): Promise<TeamSettingsResponse> {
  const { data } = await apiClient.get(`/api/v1/teams/${teamId}/settings`);
  return data;
}

export async function getTeamModelAssignments(): Promise<TeamModelAssignmentResponse[]> {
  const { data } = await apiClient.get('/api/v1/teams/model-assignments');
  return data;
}

export async function createTeamSettings(
  teamId: string,
  settingsData: CreateTeamSettingsRequest
): Promise<TeamSettingsResponse> {
  const { data } = await apiClient.post(`/api/v1/teams/${teamId}/settings`, settingsData);
  return data;
}

export async function updateTeamSettings(
  teamId: string,
  settingsData: UpdateTeamSettingsRequest
): Promise<TeamSettingsResponse> {
  const { data } = await apiClient.put(`/api/v1/teams/${teamId}/settings`, settingsData);
  return data;
}

export async function deleteTeamSettings(teamId: string): Promise<void> {
  await apiClient.delete(`/api/v1/teams/${teamId}/settings`);
}

// Admin-only: assign a team's extraction models. Separate from the team-admin settings PUT.
export async function updateTeamModelSelection(
  teamId: string,
  request: UpdateTeamModelSelectionRequest
): Promise<TeamSettingsResponse> {
  const { data } = await apiClient.put(`/api/v1/teams/${teamId}/settings/models`, request);
  return data;
}

export async function getWebhookDeliveries(
  teamId: string,
  page: number,
  pageSize: number,
  status?: string,
  eventType?: string,
  transactionId?: string
): Promise<PageOfWebhookDeliveryListItem> {
  const params: Record<string, string | number> = { page, pageSize };
  if (status) params.status = status;
  if (eventType) params.eventType = eventType;
  if (transactionId) params.transactionId = transactionId;
  const { data } = await apiClient.get(`/api/v1/teams/${teamId}/webhook/deliveries`, { params });
  return data;
}

export async function getWebhookDeliveryDetail(
  teamId: string,
  deliveryId: string
): Promise<WebhookDeliveryDetail> {
  const { data } = await apiClient.get(`/api/v1/teams/${teamId}/webhook/deliveries/${deliveryId}`);
  return data;
}

export async function retryWebhookDelivery(
  teamId: string,
  deliveryId: string
): Promise<WebhookDeliveryDetail> {
  const { data } = await apiClient.post(
    `/api/v1/teams/${teamId}/webhook/deliveries/${deliveryId}/retry`
  );
  return data;
}

export async function testWebhook(
  teamId: string,
  request?: WebhookTestRequest
): Promise<WebhookTestResponse> {
  const { data } = await apiClient.post(`/api/v1/teams/${teamId}/webhook/tests`, request ?? {});
  return data;
}
